/* eslint-disable react/prop-types */
import React from 'react'
import { Slide } from '@mui/material'
import { getScrollY } from './helpers'

/**
 * Custom hook to hide an element on scroll.
 * @param {number} threshold - The scroll threshold at which the element should be hidden.
 * @returns {boolean} - Whether the element should be hidden or not.
 */
const useHideOnScroll = (threshold: number): boolean => {
  const scrollContainer = document.getElementById('main-scroll-container')
  const scrollRef = React.useRef<number>()
  const [hide, setHide] = React.useState<boolean>(false)

  const handleScroll = React.useCallback(() => {
    const scrollY = getScrollY(scrollContainer ?? window)
    const prevScrollY = scrollRef.current
    scrollRef.current = scrollY

    if (!prevScrollY || scrollY < prevScrollY) {
      setHide(false)
    } else if (prevScrollY && scrollY > prevScrollY && scrollY > threshold) {
      setHide(true)
    } else {
      setHide(false)
    }
  }, [threshold, scrollContainer])

  React.useEffect(() => {
    scrollContainer?.addEventListener('scroll', handleScroll)
    return () => {
      scrollContainer?.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll, scrollContainer])

  return hide
}

/**
 * Hides the component on scroll based on the provided threshold.
 * @param {Object} props - The props for the component.
 * @param {ReactNode} props.children - The child components.
 * @param {number} props.threshold - The threshold value to determine when to hide the component.
 * @returns {ReactNode} - The component wrapped with the hide-on-scroll functionality.
 */
export default function HideOnScroll(
  props: Readonly<{
    children: React.ReactElement<never, never> | React.ReactElement
    threshold: number
  }>,
) {
  const { children, threshold } = props

  const hide: boolean = useHideOnScroll(threshold)

  return (
    <Slide appear={false} direction='down' in={!hide}>
      {children}
    </Slide>
  )
}
